import React, { useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, technologies, buildings, formatTime, calculateResearchTime } from '../../data';
import { getScaledTechCost } from '../../data/balance';
import { BuildingChip, TechChip, ResourceChip } from '../common';

function TechPanel({ onOpenTechTree, onNotification }) {
  const {
    playerResources,
    researchedTechs,
    buildingCounts,
    currentResearch,
    researchProgress,
    startResearch,
    currentResearchSpeed,
    cancelResearch,
    researchQueue,
    clearResearchQueue,
    playerCharacter,
  } = useGame();

  // Get available techs (checking building requirements too)
  const availableTechs = useMemo(() => {
    return Object.values(technologies).filter(tech => {
      if (researchedTechs.includes(tech.id)) return false;
      if (!tech.requires.every(req => researchedTechs.includes(req))) return false;
      if (tech.requiresBuildings) {
        for (const [bId, count] of Object.entries(tech.requiresBuildings)) {
          if ((buildingCounts[bId] || 0) < count) return false;
        }
      }
      return true;
    }).slice(0, 4); // Show top 4 available
  }, [researchedTechs, buildingCounts]);

  // Get last 3 unique completed techs (most recent first)
  const recentCompleted = useMemo(() => {
    const seen = new Set();
    const unique = [];
    for (let i = researchedTechs.length - 1; i >= 0 && unique.length < 3; i--) {
      const id = researchedTechs[i];
      if (!seen.has(id) && technologies[id]) {
        seen.add(id);
        unique.push(technologies[id]);
      }
    }
    return unique;
  }, [researchedTechs]);

  // Current research info
  const currentTech = currentResearch ? technologies[currentResearch] : null;
  const researchTime = currentTech ? calculateResearchTime(currentTech.tier, currentResearchSpeed) : 0;
  const remaining = currentTech ? Math.max(0, researchTime - (researchProgress || 0)) : 0;
  const progressPct = currentTech && researchTime > 0 ? Math.min(100, (researchProgress / researchTime) * 100) : 0;

  // Memoize inventory as a Map for O(1) lookups instead of O(n) .find() per tech
  const inventoryMap = useMemo(() => {
    const map = new Map();
    playerCharacter?.inventory?.forEach(item => {
      map.set(item.id, (map.get(item.id) || 0) + (item.quantity || 1));
    });
    return map;
  }, [playerCharacter?.inventory]);

  // Check if can afford (using scaled costs from balance.js)
  // Also checks dungeon resources from inventory
  const canAfford = (tech) => {
    const scaledCost = getScaledTechCost(tech.cost, tech.tier);
    return Object.entries(scaledCost).every(([resourceId, amount]) => {
      // Check kingdom resources first
      if (playerResources[resourceId] !== undefined) {
        return (playerResources[resourceId] || 0) >= amount;
      }

      // Check dungeon resources in inventory (materials) - O(1) lookup
      const inventoryAmount = inventoryMap.get(resourceId) || 0;
      return inventoryAmount >= amount;
    });
  };

  // Get what a tech unlocks (buildings with full data)
  const getTechUnlocks = (techId) => {
    return Object.values(buildings)
      .filter(b => b.unlockCondition?.tech === techId)
      .slice(0, 4);
  };

  // Start research
  const handleStart = (tech) => {
    if (!currentResearch && canAfford(tech)) {
      startResearch(tech.id);
      onNotification?.(`🔬 Researching ${tech.name}...`, 'info');
    }
  };

  const totalTechs = Object.keys(technologies).length;

  return (
    <div className="tech-panel" style={{ maxHeight: '100%', overflowY: 'auto', minHeight: 0 }}>
      <div className="tech-header">
        <h3>🔬 Research</h3>
        <button className="tech-tree-btn" onClick={onOpenTechTree}>
          Tech Tree 🌳 {researchedTechs.length}/{totalTechs}
        </button>
      </div>

      {/* Current Research */}
      {currentTech ? (
        <div className="tech-current">
          <div className="tech-current-info">
            <span className="tech-icon">{currentTech.icon}</span>
            <span className="tech-name">{currentTech.name}</span>
            <span className="tech-time">{formatTime(remaining)}</span>
            <button 
              className="tech-cancel-btn"
              onClick={() => {
                cancelResearch();
                onNotification?.(`❌ Cancelled ${currentTech.name} research (refunded)`, 'info');
              }}
              title="Cancel research (full refund)"
            >
              ✕
            </button>
          </div>
          <div className="tech-progress-bar">
            <div className="tech-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          {/* Show what current research unlocks */}
          {(() => {
            const unlocks = getTechUnlocks(currentTech.id);
            if (unlocks.length === 0) return null;
            return (
              <div className="tech-current-unlocks">
                <span className="tech-unlocks-label">Unlocks:</span>
                {unlocks.map(b => (
                  <BuildingChip key={b.id} id={b.id} size="small" />
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="tech-idle">Select a technology to research</div>
      )}
      
      {/* Research Queue */}
      {researchQueue.length > 0 && (
        <div className="tech-queue">
          <div className="tech-queue-header">
            <span>📋 Queue ({researchQueue.length})</span>
            <button 
              className="tech-queue-clear"
              onClick={() => {
                clearResearchQueue();
                onNotification?.('🗑️ Queue cleared', 'info');
              }}
            >
              Clear
            </button>
          </div>
          <div className="tech-queue-list">
            {researchQueue.slice(0, 3).map((techId, i) => {
              const tech = technologies[techId];
              return tech ? (
                <div key={techId} className="tech-queue-item">
                  <span className="tech-queue-num">{i + 1}</span>
                  <TechChip id={techId} size="small" />
                </div>
              ) : null;
            })}
            {researchQueue.length > 3 && (
              <div className="tech-queue-more">+{researchQueue.length - 3} more</div>
            )}
          </div>
        </div>
      )}

      {/* Available Techs */}
      <div className="tech-available">
        {availableTechs.length > 0 ? (
          availableTechs.map(tech => {
            const affordable = canAfford(tech);
            const unlocks = getTechUnlocks(tech.id);
            const reqBuildings = tech.requiresBuildings || {};
            const scaledCost = getScaledTechCost(tech.cost, tech.tier);

            return (
              <div
                key={tech.id}
                className={`tech-option ${affordable ? 'can-afford' : ''} ${currentResearch ? 'disabled' : ''}`}
                onClick={() => !currentResearch && affordable && handleStart(tech)}
              >
                <span className="tech-opt-icon">{tech.icon}</span>
                <div className="tech-opt-info">
                  <div className="tech-opt-header">
                    <div className="tech-opt-name">{tech.name}</div>
                    <span className="tech-opt-time">
                      ⏱️ {formatTime(calculateResearchTime(tech.tier, currentResearchSpeed))}
                    </span>
                  </div>
                  <div className="tech-opt-cost">
                    {Object.entries(scaledCost).map(([res, amt]) => {
                      // Check both kingdom resources and dungeon resources (inventory)
                      let have = playerResources[res] || 0;
                      if (have === 0) {
                        // Check inventory for dungeon resources
                        const inventoryItem = playerCharacter.inventory.find(item => item.id === res);
                        have = inventoryItem?.quantity || 0;
                      }
                      const ok = have >= amt;
                      return (
                        <ResourceChip
                          key={res}
                          resourceId={res}
                          amount={amt}
                          currentAmount={have}
                          canAfford={ok}
                          size="small"
                        />
                      );
                    })}
                  </div>
                  {/* Show required buildings if any - inline like Unlocks */}
                  {Object.keys(reqBuildings).length > 0 && (
                    <div className="tech-opt-req-buildings">
                      <span className="tech-opt-req-label">Requires:</span>
                      {Object.entries(reqBuildings).map(([bId, count]) => (
                        <BuildingChip key={bId} id={bId} count={count} size="small" />
                      ))}
                    </div>
                  )}
                  {/* Show what this tech unlocks */}
                  {unlocks.length > 0 && (
                    <div className="tech-opt-unlocks">
                      <span className="tech-opt-unlocks-label">Unlocks:</span>
                      {unlocks.slice(0, 3).map(b => (
                        <BuildingChip key={b.id} id={b.id} size="small" showName={false} />
                      ))}
                      {unlocks.length > 3 && <span className="tech-opt-more">+{unlocks.length - 3}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="tech-none">
            {researchedTechs.length === totalTechs
              ? '✨ All technologies researched!'
              : 'Complete prerequisites to unlock more techs'}
          </div>
        )}
      </div>

      {/* Recent Completed */}
      {recentCompleted.length > 0 && (
        <div className="tech-recent">
          <div className="tech-recent-label">Recently Completed</div>
          <div className="tech-recent-list">
            {recentCompleted.map(tech => (
              <div key={tech.id} className="tech-recent-item">
                <span className="tech-recent-check">✓</span>
                <TechChip id={tech.id} size="small" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TechPanel;
